import { useCallback, useState } from 'react';
import { AlertTriangle, Info, ShieldAlert, X, ExternalLink } from 'lucide-react';
import { useGlobalContentAlerts, type ContentAlert } from '@/hooks/useContentAlerts';
import { useI18n } from '@/i18n';

const TONE: Record<string, string> = {
  info: 'bg-primary/10 border-primary/30 text-foreground',
  warning: 'bg-amber-500/10 border-amber-500/30 text-foreground',
  critical: 'bg-destructive/10 border-destructive/30 text-foreground',
};
const ICON_TONE: Record<string, string> = {
  info: 'text-primary',
  warning: 'text-amber-500',
  critical: 'text-destructive',
};

const Icon = ({ severity, className }: { severity: string; className?: string }) => {
  if (severity === 'critical') return <ShieldAlert className={className} />;
  if (severity === 'warning') return <AlertTriangle className={className} />;
  return <Info className={className} />;
};

function pickLang(json: Record<string, string> | null | undefined, lang: string): string {
  if (!json || typeof json !== 'object') return '';
  return json[lang] || json.de || json.en || json.fr || json.it || '';
}

const DISMISS_KEY = 'scuvio.global-alerts.dismissed.v1';

function loadDismissed(): Set<string> {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = window.localStorage.getItem(DISMISS_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? new Set(parsed.filter((v) => typeof v === 'string')) : new Set();
  } catch {
    return new Set();
  }
}

function persistDismissed(set: Set<string>): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(DISMISS_KEY, JSON.stringify([...set]));
  } catch {
    /* localStorage might be blocked (private mode, quota); state still in memory. */
  }
}

const SEVERITY_RANK: Record<string, number> = { critical: 0, warning: 1, info: 2 };

/**
 * GlobalContentAlertsBanner — schmaler Banner für scope_type='global' Meldungen.
 * Wird einmalig oberhalb der App-Routes gerendert. User kann pro Meldung ausblenden
 * (LocalStorage). Datenquelle: public.content_alerts (SoT).
 */
export default function GlobalContentAlertsBanner() {
  const { locale } = useI18n();
  const alerts: ContentAlert[] = useGlobalContentAlerts();
  const [dismissed, setDismissed] = useState<Set<string>>(() => loadDismissed());

  // Stabile Reihenfolge: Severity (critical zuerst), dann Veröffentlichungsdatum.
  const visible = alerts
    .filter((a) => !dismissed.has(a.id))
    .slice()
    .sort((a, b) => {
      const sa = SEVERITY_RANK[a.severity] ?? 9;
      const sb = SEVERITY_RANK[b.severity] ?? 9;
      if (sa !== sb) return sa - sb;
      const ta = a.published_at ?? a.updated_at;
      const tb = b.published_at ?? b.updated_at;
      return (tb ?? '').localeCompare(ta ?? '');
    });
  const dismiss = useCallback((id: string) => {
    setDismissed((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      persistDismissed(next);
      return next;
    });
  }, []);

  if (visible.length === 0) return null;

  return (
    <div
      className="
        fixed left-0 right-0 z-[95]
        top-0 md:top-16
        w-full border-b border-border/60
        bg-background/95 backdrop-blur-md
        shadow-sm
      "
      role="region"
      aria-label="System notifications"
    >
      <div className="max-w-screen-xl mx-auto px-3 sm:px-4 py-2 space-y-1.5">
        {visible.map((a) => {
          const title = pickLang(a.title_json, locale);
          const summary = pickLang(a.summary_json, locale) || pickLang(a.body_json, locale);
          // Defensive: leere Meldungen (kein Titel und kein Text) nicht rendern.
          if (!title && !summary) return null;
          return (
            <div
              key={a.id}
              className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-sm ${TONE[a.severity] ?? TONE.info}`}
            >
              <Icon severity={a.severity} className={`w-4 h-4 mt-0.5 shrink-0 ${ICON_TONE[a.severity] ?? ICON_TONE.info}`} />
              <div className="flex-1 min-w-0">
                {title && <span className="font-semibold mr-1.5">{title}</span>}
                {summary && <span className="text-muted-foreground">{summary}</span>}
                {a.cta_url && (
                  <a
                    href={a.cta_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-0.5 ml-2 text-primary hover:underline"
                  >
                    Mehr <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  dismiss(a.id);
                }}
                aria-label="Meldung ausblenden"
                className="shrink-0 -m-1 p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-foreground/5 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
